import { supabase } from "../lib/supabase";
import {
  isExpectedOperationalNoise,
  isNetworkFailure,
} from "../utils/errorNoise";
import {
  sanitizeForLog,
  sanitizeString,
  sanitizeUrl,
} from "../utils/logSanitizer";

// Debounce duplicate errors. Console/network failures can fire repeatedly
// during one transient outage, so keep this long enough to prevent log floods.
const recentErrors = new Map();
const DEDUP_MS = 30 * 60 * 1000;
const MAX_TRACKED_ERRORS = 250;
const MAX_LOGS_PER_SESSION_KEY = 2;

function compactErrorKeyPart(value) {
  return String(value || "")
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":uuid",
    )
    .replace(/\?.*$/, "")
    .slice(0, 180);
}

function routeForLog() {
  if (typeof window === "undefined") return "";
  return window.location?.pathname || "";
}

function isDuplicate(key) {
  const last = recentErrors.get(key);
  if (last && Date.now() - last < DEDUP_MS) return true;

  if (recentErrors.size > MAX_TRACKED_ERRORS) {
    const oldest = [...recentErrors.entries()].sort((a, b) => a[1] - b[1])[0];
    if (oldest) recentErrors.delete(oldest[0]);
  }

  recentErrors.set(key, Date.now());
  return false;
}

function sessionCount(key) {
  if (typeof sessionStorage === "undefined") return 1;
  try {
    const storageKey = `error-log-count:${key}`;
    const count = Number(sessionStorage.getItem(storageKey) || "0") + 1;
    sessionStorage.setItem(storageKey, String(count));
    return count;
  } catch {
    return 1;
  }
}

function normalizeErrorMessage(error) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const message = error.message || error.error;
    if (typeof message === "string" && message.trim()) return message;

    const details = [error.code, error.details, error.hint]
      .filter(
        (value) => typeof value === "string" || typeof value === "number",
      )
      .map(String);
    if (details.length > 0) return details.join(" | ");

    try {
      const serialized = JSON.stringify(sanitizeForLog(error));
      if (serialized && serialized !== "{}") return serialized;
    } catch {
      // Fall through to the stable fallback below.
    }
    return "Unknown error object";
  }
  return String(error);
}

function isIgnorableBrowserNoise(message, metadata = {}) {
  const text = `${message || ""} ${metadata.filename || ""}`.toLowerCase();
  return (
    text.includes("resizeobserver loop completed with undelivered notifications") ||
    text.includes("translate.googleapis.com/element/log") ||
    text.includes("static.cloudflareinsights.com/beacon") ||
    text.includes("chrome-extension://") ||
    text.includes("moz-extension://") ||
    text.includes("safari-web-extension://")
  );
}

function isExpectedAuthRefreshNoise(args) {
  if (routeForLog() !== "/login") return false;

  const text = args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error) return arg.message;
      if (arg && typeof arg === "object" && "message" in arg) {
        return String(arg.message);
      }
      return "";
    })
    .join(" ");

  return /Invalid Refresh Token:\s*(Already Used|Refresh Token Not Found)/i.test(
    text,
  );
}

function isExpectedScormConsoleNoise(args) {
  const text = args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error) return arg.message;
      if (arg && typeof arg === "object" && "message" in arg) {
        return String(arg.message);
      }
      return "";
    })
    .join(" ");

  if (!/SCORM Error|throwSCORMError/i.test(text)) return false;

  const isKnownProbeElement =
    /cmi\.core\.mastery_score/i.test(text) ||
    /cmi\.student_preference\.mastery_score/i.test(text) ||
    /cmi\.core\.exit/i.test(text);

  return (
    isKnownProbeElement &&
    (/not a valid SCORM data model element/i.test(text) ||
      /Element is write only/i.test(text) ||
      /\b(101|404)\b/.test(text))
  );
}

async function getUserId() {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Extract useful debugging info from Supabase FunctionsHttpError.
 * These errors have a generic message but carry the real error in the response body.
 */
async function extractSupabaseErrorContext(error) {
  const context = {};

  // FunctionsHttpError — extract status + response body
  if (
    error?.name === "FunctionsHttpError" ||
    error?.constructor?.name === "FunctionsHttpError"
  ) {
    try {
      // Supabase stores the response on the error object
      const response = error.context;
      if (response instanceof Response) {
        context.statusCode = response.status;
        const body = await response
          .clone()
          .json()
          .catch(() => null);
        if (body) context.responsePayload = body;
      }
    } catch {
      /* ignore */
    }
  }

  // FunctionsFetchError — network-level failure
  if (error?.name === "FunctionsFetchError") {
    context.statusCode = 0;
    context.metadata = { errorType: "network_failure" };
  }

  return context;
}

/**
 * Log any error with full context — works for frontend crashes, runtime errors,
 * and API failures. One table, all the info you need to resolve it.
 *
 * @param {Error|string} error - The error object or message
 * @param {Object} context
 * @param {string} context.type - crash | runtime | unhandled | unhandled_rejection | api | caught
 * @param {string} context.source - frontend | api | edge_function
 * @param {string} context.functionName - Which function/service failed
 * @param {number} context.statusCode - HTTP status from API response
 * @param {Object} context.requestPayload - What was sent (auto-sanitized)
 * @param {Object} context.responsePayload - What came back
 * @param {string} context.severity - error | warning | critical
 * @param {string} context.componentStack - React component stack (from ErrorBoundary)
 * @param {Object} context.metadata - Any extra context
 */
export async function logError(error, context = {}) {
  const message = normalizeErrorMessage(error);
  const metadata = context.metadata || {};
  if (isIgnorableBrowserNoise(message, metadata)) return;
  if (isExpectedAuthRefreshNoise([message])) return;
  if (
    isExpectedOperationalNoise(message, {
      userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
      errorName: error?.name || "",
    })
  ) {
    return;
  }

  const route = routeForLog();
  const normalizedMessage = isNetworkFailure(message)
    ? "generic-network-failure"
    : compactErrorKeyPart(message);
  const dedupKey = [
    context.type || "runtime",
    context.functionName || "",
    route,
    context.statusCode || "",
    normalizedMessage,
  ]
    .map(compactErrorKeyPart)
    .join(":");

  if (isDuplicate(dedupKey)) return;
  if (sessionCount(dedupKey) > MAX_LOGS_PER_SESSION_KEY) return;

  // Auto-extract context from Supabase errors
  const supabaseCtx = await extractSupabaseErrorContext(error);

  try {
    await supabase.from("error_logs").insert({
      user_id: await getUserId(),
      type: context.type || "runtime",
      source: context.source || "frontend",
      severity: context.severity || "error",
      // Every field is scrubbed: payloads and metadata recursively, the URL
      // without its hash or credential parameters (token_hash links).
      error_message: sanitizeString(message),
      error_stack: sanitizeString(error?.stack) || null,
      component_stack: sanitizeString(context.componentStack) || null,
      function_name: sanitizeString(context.functionName) || null,
      status_code: context.statusCode || supabaseCtx.statusCode || null,
      request_payload: context.requestPayload
        ? sanitizeForLog(context.requestPayload)
        : null,
      response_payload: sanitizeForLog(
        context.responsePayload || supabaseCtx.responsePayload || null,
      ),
      url: sanitizeUrl(window.location.href),
      route: sanitizeString(window.location.pathname),
      user_agent: navigator.userAgent,
      metadata: sanitizeForLog({
        ...metadata,
        ...supabaseCtx.metadata,
        timestamp: new Date().toISOString(),
        dedupKey,
      }),
    });
  } catch (_) {
    // Never let logging break the app
  }
}

/**
 * Shorthand for logging API/edge function errors with request+response context.
 *
 * Usage:
 *   catch (error) {
 *     logApiError("inviteInstructor", error, { partnerId }, responseData);
 *     toast.error(error.message);
 *   }
 */
export async function logApiError(
  functionName,
  error,
  requestPayload,
  responsePayload,
) {
  return logError(error, {
    type: "api",
    source: "api",
    functionName,
    statusCode: error?.status || responsePayload?.status || null,
    requestPayload,
    responsePayload,
  });
}

/**
 * Shorthand for logging caught frontend errors with context.
 *
 * Usage:
 *   catch (error) {
 *     logRuntimeError(error, { action: "loadCourses", courseId });
 *     toast.error(error.message);
 *   }
 */
export async function logRuntimeError(error, metadata = {}) {
  return logError(error, {
    type: "runtime",
    source: "frontend",
    metadata,
  });
}

// ── Global unhandled error listeners ──

/**
 * Infer function/action name from console.error string prefix.
 * Most catch blocks write: console.error('Error doing X:', error)
 * or: console.error('X error:', error)
 */
function inferFunctionName(args) {
  const stringArgs = args.filter((a) => typeof a === "string");
  if (stringArgs.length === 0) return null;

  const label = stringArgs[0];
  // "Error fetching partner instructors:" → "fetching partner instructors"
  // "Upload error:" → "Upload"
  const match = label.match(/^(?:Error\s+)?(.+?)(?:\s*error)?:?\s*$/i);
  return match?.[1]?.trim() || label.replace(/:$/, "").trim();
}

export function initGlobalErrorHandlers() {
  // Unhandled JS errors
  window.addEventListener("error", (event) => {
    if (event.error?.__ErrorBoundary) return;
    if (
      isIgnorableBrowserNoise(event.message, {
        filename: event.filename,
      })
    ) {
      return;
    }

    logError(event.error || event.message, {
      type: "unhandled",
      source: "frontend",
      severity: "critical",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    logError(event.reason, {
      type: "unhandled_rejection",
      source: "frontend",
      severity: "critical",
    });
  });

  // Intercept console.error to also log caught errors to error_logs table.
  // This captures the 100+ catch blocks that do console.error + toast.error
  // but never call logError/logApiError directly.
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Extract the error-like argument
    const errorArg = args.find((a) => a instanceof Error);
    const messageArg = args.find((a) => typeof a === "string");

    if (isExpectedScormConsoleNoise(args)) return;
    if (isExpectedAuthRefreshNoise(args)) {
      originalConsoleError.apply(console, args);
      return;
    }

    originalConsoleError.apply(console, args);

    // Skip React/framework noise (internal warnings, DevTools, HMR)
    if (
      messageArg &&
      /^(Warning:|Download the React DevTools|%c|%s|\[HMR\]|\[vite\])/.test(
        messageArg,
      )
    )
      return;

    // Skip known handled errors from third-party libraries (logged via console.error before throwing)
    if (messageArg && /unable to create webgl context/i.test(messageArg))
      return;
    // Skip SCORM runtime errors (third-party courseware calling write-only elements — expected behavior)
    if (messageArg && /SCORM Error|throwSCORMError/i.test(messageArg)) return;

    // Build the error — prefer Error objects, fall back to Supabase error-like objects, then strings
    let error = errorArg;
    if (!error) {
      // Supabase query errors are plain objects with .message
      const objArg = args.find(
        (a) => a && typeof a === "object" && a.message && !(a instanceof Error),
      );
      if (objArg) {
        error = new Error(objArg.message);
        if (objArg.code) error.code = objArg.code;
        if (objArg.details) error.details = objArg.details;
        if (objArg.hint) error.hint = objArg.hint;
      } else if (messageArg) {
        error = new Error(messageArg);
      }
    }
    if (!error) return;

    // Infer context from the console.error prefix string
    const inferredName = inferFunctionName(args);

    // Extract Supabase error details (code, details, hint) from plain object args
    const supabaseObj = args.find(
      (a) =>
        a &&
        typeof a === "object" &&
        !(a instanceof Error) &&
        (a.code || a.details || a.hint),
    );
    const supabaseMeta = supabaseObj
      ? {
          pgCode: supabaseObj.code || undefined,
          details: supabaseObj.details || undefined,
          hint: supabaseObj.hint || undefined,
        }
      : {};

    logError(error, {
      type: "caught",
      source: "frontend",
      severity: "warning",
      functionName: inferredName,
      metadata: {
        capturedVia: "console.error",
        ...supabaseMeta,
      },
    });
  };
}
